/**
 * Leitura da grade do CEMADEN no instante do quadro exibido — funções puras
 * (testadas em `series.test.ts`).
 *
 * É o que faz a chuva "andar" junto com a animação: a cada quadro do loop, a
 * camada pergunta a esta função quanto choveu em cada estação até aquele
 * instante, do mesmo jeito que os focos perguntam a `fires/timeFilter.ts`.
 *
 * A distinção entre `SEM_DADO` e `0` é levada a sério em todo o arquivo: uma
 * estação calada não é uma estação que mediu chuva zero, e desenhar as duas
 * igual seria afirmar que não choveu onde ninguém olhou.
 */
import { SEM_DADO, type Grade, type Grandeza } from './types';

/**
 * Faixas da escala de cor, em mm. Os cortes seguem a prática de boletim
 * meteorológico no Brasil (chuva fraca / moderada / forte / muito forte).
 */
export const FAIXAS: readonly { min: number; cor: string; rotulo: string }[] = [
  { min: 0, cor: '#2b3a4a', rotulo: 'sem chuva' },
  { min: 0.2, cor: '#3b82c4', rotulo: 'fraca' },
  { min: 1, cor: '#2bb3c0', rotulo: 'leve' },
  { min: 5, cor: '#3fbf6f', rotulo: 'moderada' },
  { min: 10, cor: '#d9c53a', rotulo: 'forte' },
  { min: 20, cor: '#e8803a', rotulo: 'muito forte' },
  { min: 50, cor: '#d6455c', rotulo: 'extrema' },
  { min: 100, cor: '#a742c9', rotulo: 'excepcional' },
];

/** Índice da faixa de um acumulado em mm. */
export function faixaDe(mm: number): number {
  let i = 0;
  for (let k = 1; k < FAIXAS.length; k++) {
    if (mm >= FAIXAS[k]!.min) i = k;
    else break;
  }
  return i;
}

export function corDe(mm: number): string {
  return FAIXAS[faixaDe(mm)]!.cor;
}

/** Fatia da grade correspondente a `ms`; -1 quando está fora da janela. */
export function slotDe(grade: Grade, ms: number): number {
  const passo = grade.passoMin * 60_000;
  const i = Math.floor((ms - grade.t0) / passo);
  return i >= 0 && i < grade.slots ? i : -1;
}

/** Instante (epoch ms) do fim da fatia `slot`. */
export function instanteDe(grade: Grade, slot: number): number {
  return grade.t0 + slot * grade.passoMin * 60_000;
}

export interface Leitura {
  /** Acumulado em mm, ou null quando não houve nenhuma medição na janela. */
  mm: number | null;
  /** Quantas fatias da janela tinham medição. */
  medidas: number;
  /** Tamanho da janela em fatias. */
  fatias: number;
}

export const SEM_LEITURA: Leitura = { mm: null, medidas: 0, fatias: 0 };

/**
 * Chuva total de uma estação no PERÍODO INTEIRO da grade — as 24 h que o
 * CEMADEN publica.
 *
 * Não há janela a escolher: 24 h é o que a fonte entrega, e oferecer
 * "acumular em 1 h" sobre um dado de 24 h era uma escolha sem lastro.
 *
 * É o que colore a gota no mapa, e é de propósito que não dependa do quadro:
 * com 569 pontos, recolorir todos a cada quadro vira pisca-pisca e esconde a
 * informação em vez de mostrá-la. A cor responde "choveu aqui nas últimas
 * 24 h?", que é uma pergunta estável; o que varia com o tempo é o halo (ver
 * `chuvaNaFatia`).
 */
export function totalDoPeriodo(linha: readonly number[] | undefined, grandeza: Grandeza): Leitura {
  if (!linha?.length) return SEM_LEITURA;

  if (grandeza === 'acum24h') {
    // A célula já é o acumulado de 24 h: o total do período é a leitura mais
    // recente que existir, não a soma delas.
    for (let i = linha.length - 1; i >= 0; i--) {
      if (linha[i] !== SEM_DADO) return { mm: linha[i]!, medidas: 1, fatias: 1 };
    }
    return { mm: null, medidas: 0, fatias: linha.length };
  }

  let soma = 0;
  let medidas = 0;
  for (const v of linha) {
    if (v === SEM_DADO) continue;
    soma += v;
    medidas++;
  }
  return medidas === 0 ? { mm: null, medidas: 0, fatias: linha.length } : { mm: soma, medidas, fatias: linha.length };
}

/**
 * Chuva caída NA FATIA do quadro exibido — o sinal que pisca no mapa.
 *
 * `null` = não dá para saber (fatia sem dado, ou sem a fatia anterior para
 * comparar em `acum24h`). Diferente de `0`, que é "mediu e não choveu".
 *
 * Com `acum24h` não existe medida instantânea, mas a variação do acumulado de
 * uma fatia para a seguinte revela chuva nova. A janela de 24 h também descarta
 * chuva velha pelo outro lado, então uma variação negativa é a saída de chuva
 * antiga, e não chuva medida: só o aumento conta.
 */
export function chuvaNaFatia(
  linha: readonly number[] | undefined,
  slot: number,
  grandeza: Grandeza,
): number | null {
  if (!linha || slot < 0 || slot >= linha.length) return null;
  const v = linha[slot]!;
  if (v === SEM_DADO) return null;

  if (grandeza === 'chuva10min') return v > 0 ? v : 0;

  // Com `acum24h` a chuva nova é a diferença para a fatia anterior. Sem ela
  // não dá para calcular diferença nenhuma: o resultado é DESCONHECIDO, não
  // zero. Devolver 0 aqui apagaria 35 mm de chuva real numa grade
  // `[5, 5, SEM_DADO, 40]` e ainda desenharia "mediu e não choveu".
  const anterior = slot > 0 ? linha[slot - 1]! : SEM_DADO;
  if (anterior === SEM_DADO) return null;
  const d = v - anterior;
  return d > 0 ? d : 0;
}

/**
 * Raio da gota em px. Cresce com a chuva, mas devagar (raiz quarta): sem
 * isso, 100 mm ofuscaria completamente 5 mm, que já é chuva relevante.
 *
 * A faixa vai de ~0,6 a ~1,3 vez a base. Era de 0,7 a 1,8, e as gotas com
 * chuva ficavam quase três vezes maiores que as secas — viravam manchas que
 * cobriam os focos e as cidades em vez de marcar um ponto.
 */
export function raioDe(mm: number, base: number): number {
  if (mm <= 0) return base * 0.55;
  const k = Math.min(1, (mm / 60) ** 0.25);
  return base * (0.6 + 0.7 * k);
}

/** Resumo para o painel: quantas estações reportaram e quantas com chuva. */
export function resumir(leituras: readonly Leitura[]): { total: number; comDado: number; comChuva: number; maior: number } {
  let comDado = 0;
  let comChuva = 0;
  let maior = 0;
  for (const l of leituras) {
    if (l.mm === null) continue;
    comDado++;
    if (l.mm > 0) comChuva++;
    if (l.mm > maior) maior = l.mm;
  }
  return { total: leituras.length, comDado, comChuva, maior };
}
